import axios, { AxiosError, type AxiosInstance } from "axios";
import type { ApiError } from "@/types/api";
import { sanitizeApiErrorMessage } from "@/utils/apiErrors";

const baseURL = import.meta.env.VITE_API_BASE_URL ?? "/api/ui/v1";
const withCredentials = import.meta.env.VITE_API_WITH_CREDENTIALS === "true";

/**
 * Singleton axios instance configured for the Atlas UI backend.
 *
 * - prefixes all requests with /api/ui/v1 (override via VITE_API_BASE_URL)
 * - normalises backend error envelopes (UiErrorResponse) into a consistent ApiError
 * - leaves auth header injection to a future interceptor — UI runs behind the
 *   gateway today, so we do not attach a token from localStorage
 */
import { useSettingsStore } from "@/store/settingsStore";
import { showActionError } from "@/lib/feedback";
import { handleSessionExpired } from "@/lib/authSession";

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 30_000,
  withCredentials,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/** Normalised path without query string or trailing slash. */
function requestPath(url: string): string {
  return (url.split("?")[0] ?? "").replace(/\/$/, "") || "/";
}

/** True for GETs keyed only by path id — skip list-style legalEntity/branch query injection. */
function isSingletonDetailGet(method: string | undefined, url: string): boolean {
  if (method?.toLowerCase() !== "get") return false;
  const path = requestPath(url);
  return /^\/diary-entries\/[^/]+$/.test(path) || /^\/properties\/[^/]+$/.test(path);
}

/** Contact/property list searches are branch-scoped, not legal-entity-scoped. */
function isBranchScopedListGet(method: string | undefined, url: string): boolean {
  if (method?.toLowerCase() !== "get") return false;
  const path = requestPath(url);
  return path === "/contacts" || path === "/properties";
}

apiClient.interceptors.request.use((config) => {
  // Inject Keycloak token if available
  const keycloak = (window as any).keycloak;
  if (keycloak?.token) {
    config.headers.Authorization = `Bearer ${keycloak.token}`;
  }

  // Inject mock user email if mock auth is active
  if (import.meta.env.VITE_USE_MOCK_AUTH === "true" && keycloak?.token === "mock-token") {
    config.headers["X-Mock-User-Email"] = import.meta.env.VITE_MOCK_USER_EMAIL || "test@bridgeai.co.uk";
  }

  // Automatically inject global context (legal entity, branch) into all GET requests
  // unless explicitly provided or hitting the context-selection endpoints themselves.
  if (
    config.method?.toLowerCase() === "get" &&
    config.url &&
    !config.url.includes("/legal-entities") &&
    !isSingletonDetailGet(config.method, config.url)
  ) {
    const { legalEntityId, branchId } = useSettingsStore.getState();
    config.params = config.params || {};

    // Requests whose scope comes from a form rather than the header opt out of
    // the global context entirely — an unselected ("All") scope has to stay
    // absent from the query instead of being back-filled from the header.
    const skipGlobalContext = Boolean(config.params.skipGlobalContext);
    delete config.params.skipGlobalContext;
    if (skipGlobalContext) return config;

    const isLead = config.url.includes("/leads");
    const isBranchList = config.url.includes("/branches");
    const isOrgContext = config.url.includes("/profile") || config.url.includes("/communication-channels");
    const skipGlobalBranchId = Boolean(config.params.skipGlobalBranchId);
    delete config.params.skipGlobalBranchId;

    const branchScopedList = isBranchScopedListGet(config.method, config.url);

    if (branchScopedList) {
      const effectiveBranchId =
        (typeof config.params.branchId === "string" ? config.params.branchId.trim() : "") ||
        branchId?.trim() ||
        "";
      if (effectiveBranchId) {
        config.params.branchId = effectiveBranchId;
        delete config.params.legalEntityId;
      } else {
        delete config.params.branchId;
        if (legalEntityId && config.params.legalEntityId === undefined) {
          config.params.legalEntityId = legalEntityId;
        }
      }
    } else {
      if (legalEntityId && config.params.legalEntityId === undefined) {
        config.params.legalEntityId = legalEntityId;
      }
      if (
        branchId &&
        !skipGlobalBranchId &&
        !isLead &&
        !isBranchList &&
        !isOrgContext &&
        config.params.branchId === undefined
      ) {
        config.params.branchId = branchId;
      }
    }
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    const data = error.response?.data as (ApiError & { error_description?: string; error?: string }) | undefined;
    let message = sanitizeApiErrorMessage(
      data?.message ?? data?.error_description ?? error.message,
    );

    if (
      !data?.message &&
      data?.error === "invalidPasswordMinSpecialCharsMessage" &&
      !data?.error_description
    ) {
      message = "Invalid password: must contain at least 1 special characters.";
    }

    if (error.response?.status === 401) {
      handleSessionExpired();
      message = "Your session has expired. Please sign in again.";
    } else if (error.response?.status === 403) {
      message = "You are not allowed to perform this action or do not have the necessary role.";
      showActionError(message, { title: "Access denied" });
    }

    const normalized: ApiError = {
      status: error.response?.status ?? 0,
      code: data?.code ?? "NETWORK_ERROR",
      message,
      path: data?.path ?? error.config?.url ?? "",
      timestamp: data?.timestamp ?? new Date().toISOString(),
      // Preserved for endpoints that answer errors with a domain DTO rather
      // than the shared UiErrorResponse envelope — see ApiError.body.
      body: error.response?.data,
    };
    return Promise.reject(normalized);
  },
);

/** Strip null/undefined values so axios skips empty query params. */
export function clean<T extends object>(obj: T | undefined): Record<string, any> {
  if (!obj) return {};
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined || v === "") continue;
    out[k] = v;
  }
  return out;
}
